import React, { useEffect, useMemo, useState } from "react";

import useTheme from "hooks/observer";

import { humanBytes, relativeTime } from "./_shared/utils";
import { useYsentinel } from "../data/YsentinelDataContext";
import { rangeWindow } from "../data/defaults";

import { ChartCard, Badge, EmptyHint, ErrorHint, Loader } from "./_shared/primitives";
import EChart, { echartsPalette, TABLEAU_10 } from "./_shared/EChart";
import StepNavigator from "./_shared/StepNavigator";
import FileSearchModal from "./FileSearchModal";
import "./_shared/dashboard.scss";
import "./_shared/step-navigator.scss";
import "./files-dashboard.scss";

import {
  GetFileDetails, GetFileSummary, GetLifecycleEvents, GetVersionHistory,
  GetShares, GetPathHistory, GetActorStats, GetCountryStats,
} from "../data/queuedSdk";


// =============================================================================
// Helpers
// =============================================================================

const FILE_TYPE_ICON = {
  folder: "📁", doc: "📝", sheet: "📊", pdf: "📕",
  image: "🖼", video: "🎬", audio: "🔊", csv: "📋",
  figma: "🎨", archive: "📦",
};
const iconFor = (type) => FILE_TYPE_ICON[type] || "📄";

const fmtDate = (at) =>
  new Date(at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const fmtTime = (at) =>
  new Date(at).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });

// Categorize ops for the operation-mix donut.
const OP_CATEGORY = {
  StartWrite: "creation", CreateFolder: "creation", CopyFile: "creation",
  FinalizeWrite: "modification", RestoreVersion: "modification", UntrashFile: "modification",
  UpdateTags: "modification", AddComment: "modification", UpdateComment: "modification", RemoveComment: "modification",
  Share: "sharing", UpdateShare: "sharing",
  MoveFile: "location", MoveFolder: "location", RenameFile: "location", RenameFolder: "location",
  GetChunk: "read",
  RemoveVersion: "destructive", RemoveFile: "destructive",
};

const VERSION_KIND = {
  Created:  { color: "#10b981", icon: "＋" },
  Updated:  { color: "#3b82f6", icon: "✎" },
  Restored: { color: "#0891b2", icon: "↺" },
  Removed:  { color: "#dc2626", icon: "🗑" },
  Deleted:  { color: "#7f1d1d", icon: "🗑" },
};

const SDK_VERSION_KIND = {
  1: "Created",
  2: "Updated",
  3: "Restored",
  4: "Removed",
  5: "Deleted",
};

const deriveVersions = (events) => {
  const relevant = events.filter((e) => ["FinalizeWrite", "RestoreVersion", "UntrashFile", "RemoveVersion", "RemoveFile"].includes(e.op));
  let writeCount = 0;
  return relevant.map((e) => {
    let kind;
    let version;
    let size = "";
    if (e.op === "FinalizeWrite") {
      writeCount++;
      kind = writeCount === 1 ? "Created" : "Updated";
      const m = /^v(\d+)\s*•\s*(.+)$/.exec(e.detail || "");
      version = m ? `v${m[1]}` : `v${writeCount}`;
      size = m ? m[2] : "";
    } else if (e.op === "RestoreVersion" || e.op === "UntrashFile") {
      kind = "Restored";
      version = "↺";
    } else if (e.op === "RemoveFile") {
      kind = "Deleted";
      version = "✗";
    } else {
      kind = "Removed";
      version = "×";
    }
    return { kind, version, at: e.at, actor: e.actor, size, label: e.detail || "" };
  });
};

// ── Granted-by (Option A): Permission Source Cards ──
const GrantedBySourceCard = ({ g }) => {
  if (!g) return <span className="ysd-mono">—</span>;

  // Fallback for raw string
  if (typeof g === "string") {
    return (
      <div className="ysd-grant-source-card ysd-grant-source-card--neutral">
        <span className="ysd-grant-source-card__icon">👤</span>
        <div className="ysd-grant-source-card__body">
          <small>USER</small>
          <strong>{g}</strong>
        </div>
      </div>
    );
  }

  const cards = [];

  if (g.is_self) {
    cards.push(
      <div key="self" className="ysd-grant-source-card ysd-grant-source-card--success">
        <span className="ysd-grant-source-card__icon">🤝</span>
        <div className="ysd-grant-source-card__body">
          <small>SELF</small>
          <strong>Owner</strong>
        </div>
      </div>
    );
  }

  if (Array.isArray(g.teams) && g.teams.length) {
    g.teams.forEach(t => {
      cards.push(
        <div key={`t-${t}`} className="ysd-grant-source-card ysd-grant-source-card--info">
          <span className="ysd-grant-source-card__icon">👥</span>
          <div className="ysd-grant-source-card__body">
            <small>TEAM</small>
            <strong>{t}</strong>
          </div>
        </div>
      );
    });
  }

  if (Array.isArray(g.roles) && g.roles.length) {
    g.roles.forEach(r => {
      cards.push(
        <div key={`r-${r}`} className="ysd-grant-source-card ysd-grant-source-card--brand">
          <span className="ysd-grant-source-card__icon">🛡</span>
          <div className="ysd-grant-source-card__body">
            <small>ROLE</small>
            <strong>{r}</strong>
          </div>
        </div>
      );
    });
  }

  if (g._raw) {
    cards.push(
      <div key="raw" className="ysd-grant-source-card ysd-grant-source-card--neutral">
        <span className="ysd-grant-source-card__icon">📝</span>
        <div className="ysd-grant-source-card__body">
          <small>RAW</small>
          <strong>{g._raw}</strong>
        </div>
      </div>
    );
  }

  return cards.length > 0
    ? <div className="ysd-grant-source-cards-wrap">{cards}</div>
    : <span className="ysd-mono">—</span>;
};

const grantedByFlat = (g) => {
  if (!g) return "—";
  if (typeof g === "string") return g;
  const parts = [];
  if (g.is_self) parts.push("self");
  (g.teams || []).forEach(t => parts.push(`#${t}`));
  (g.roles || []).forEach(r => parts.push(r));
  console.log("granted by", g, parts);
  return parts.join(", ") || g._raw || "—";
};

// Truncate version identifiers (UUIDs / long hashes) to fit the 52 px circle.
const truncVersion = (s) => {
  if (!s) return "?";
  const str = String(s);
  // UUID pattern — strip dashes and take first 6 hex chars
  if (/^[0-9a-f-]{32,}$/i.test(str)) return str.replace(/-/g, "").slice(0, 6);
  if (str.length <= 8) return str;
  return str.slice(0, 6) + "…";
};


// =============================================================================
// Component
// =============================================================================

const FilesDashboard = () => {
  const mode = useTheme();
  const palette = echartsPalette(mode);

  const {
    range,
    files: filesSlice,
  } = useYsentinel();

  const {
    files: allFiles,
    fileEvents,
    currentShares,
    pathHistory,
    externalExposure,
    selectedFileUuid,
    breadcrumb,
    setSelectedFile,
    pushBranch,
    popToBreadcrumb,
  } = filesSlice;

  const fileFromAll = useMemo(
    () => allFiles.find((f) => f.uuid === selectedFileUuid) || null,
    [allFiles, selectedFileUuid],
  );

  // --- SDK State ---
  const [sdkFile, setSdkFile] = useState(null);
  const [loadingFile, setLoadingFile] = useState(false);
  const [sdkEvents, setSdkEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [sdkSummary, setSdkSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [sdkVersions, setSdkVersions] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [sdkShares, setSdkShares] = useState([]);
  const [loadingShares, setLoadingShares] = useState(false);
  const [sdkPaths, setSdkPaths] = useState([]);
  const [loadingPaths, setLoadingPaths] = useState(false);
  const [sdkActors, setSdkActors] = useState([]);
  const [loadingActors, setLoadingActors] = useState(false);
  const [sdkCountries, setSdkCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);

  const [errorFile, setErrorFile] = useState(null);
  const [errorSummary, setErrorSummary] = useState(null);
  const [errorEvents, setErrorEvents] = useState(null);
  const [errorVersions, setErrorVersions] = useState(null);
  const [errorShares, setErrorShares] = useState(null);
  const [errorPaths, setErrorPaths] = useState(null);
  const [errorActors, setErrorActors] = useState(null);
  const [errorCountries, setErrorCountries] = useState(null);


  const file = sdkFile || fileFromAll;

  useEffect(() => {
    if (!selectedFileUuid) return;
    let active = true;
    const { start, end } = rangeWindow(range);

    setLoadingFile(true);
    setErrorFile(null);
    GetFileDetails(selectedFileUuid, start, end)
      .then(d => active && setSdkFile(d))
      .catch(() => active && setErrorFile("Failed to load file details"))
      .finally(() => active && setLoadingFile(false));

    setLoadingSummary(true);
    setErrorSummary(null);
    GetFileSummary(selectedFileUuid, start, end)
      .then(d => active && setSdkSummary(d))
      .catch(() => active && setErrorSummary("Failed to load file summary"))
      .finally(() => active && setLoadingSummary(false));

    setLoadingEvents(true);
    setErrorEvents(null);
    GetLifecycleEvents(selectedFileUuid, start, end)
      .then(d => {
        if (!active) return;
        const normalised = d.map(e => ({
          ...e,
          op: e.operation || e.op || "",
          at: e.timestamp ? new Date(Number(e.timestamp)).toISOString() : (e.at || ""),
          success: e.result !== undefined ? (e.result === 1) : e.success,
          permission_granted_by: e.grantedBy ? { is_self: false, _raw: e.grantedBy } : e.permission_granted_by,
          destination_file_id: e.destinationFileId || e.destination_file_id || null,
        }));
        setSdkEvents(normalised);
      })
      .catch(() => active && setErrorEvents("Failed to load events"))
      .finally(() => active && setLoadingEvents(false));

    setLoadingVersions(true);
    setErrorVersions(null);
    GetVersionHistory(selectedFileUuid, start, end)
      .then(d => active && setSdkVersions(d))
      .catch(() => active && setErrorVersions("Failed to load versions"))
      .finally(() => active && setLoadingVersions(false));

    setLoadingShares(true);
    setErrorShares(null);
    GetShares(selectedFileUuid, start, end)
      .then(d => active && setSdkShares(d))
      .catch(() => active && setErrorShares("Failed to load shares"))
      .finally(() => active && setLoadingShares(false));

    setLoadingPaths(true);
    setErrorPaths(null);
    GetPathHistory(selectedFileUuid, start, end)
      .then(d => active && setSdkPaths(d))
      .catch(() => active && setErrorPaths("Failed to load path history"))
      .finally(() => active && setLoadingPaths(false));

    setLoadingActors(true);
    setErrorActors(null);
    GetActorStats(selectedFileUuid, start, end)
      .then(d => active && setSdkActors(d))
      .catch(() => active && setErrorActors("Failed to load actor stats"))
      .finally(() => active && setLoadingActors(false));

    setLoadingCountries(true);
    setErrorCountries(null);
    GetCountryStats(selectedFileUuid, start, end)
      .then(d => active && setSdkCountries(d))
      .catch(() => active && setErrorCountries("Failed to load country stats"))
      .finally(() => active && setLoadingCountries(false));


    return () => { active = false; };
  }, [selectedFileUuid, range]);

  const events = useMemo(
    () => (sdkEvents.length > 0 ? sdkEvents : (fileFromAll ? (fileEvents[fileFromAll.uuid] || []) : [])).slice().sort((a, b) => Number(a.timestamp || Date.parse(a.at)) - Number(b.timestamp || Date.parse(b.at))),
    [fileFromAll, sdkEvents, fileEvents],
  );

  const derivedStatus = useMemo(() => {
    let lastRemoveIdx = -1;
    let restoredAfterRemove = false;

    events.forEach((e, idx) => {
      if (e.op === "RemoveFile") {
        lastRemoveIdx = idx;
        restoredAfterRemove = false; // reset
      }
      if (e.op === "UntrashFile" && e.success && idx > lastRemoveIdx && lastRemoveIdx !== -1) {
        restoredAfterRemove = true;
      }
    });

    if (lastRemoveIdx === -1) return null;
    return restoredAfterRemove ? "ALIVE" : "REMOVED";
  }, [events]);

  // Re-open the picker on demand. ⌘K / Ctrl+K shortcut.
  const [pickerOpen, setPickerOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      const mac = navigator.platform.toUpperCase().includes("MAC");
      if ((mac ? e.metaKey : e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPickerOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // ── Data-driven PDF report (lazy-load jsPDF + autoTable so they don't
  //    bloat the bundle for users who never click Export)
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    if (!file) return;
    setExporting(true);
    try {
      const { jsPDF } = await import("jspdf");
      const autoTableMod = await import("jspdf-autotable");
      const autoTable = autoTableMod.default || autoTableMod.autoTable || autoTableMod;
      const { renderChartToImage, addChartImage } = await import("./_shared/chartImage");

      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
      const pageW = pdf.internal.pageSize.getWidth();
      const margin = 36;
      const brand = [130, 55, 187];

      // PDF-fixed light palette so the export reads the same in any theme.
      const pdfPalette = {
        text: "#374151", textSoft: "#6b7280", grid: "#e5e7eb",
        cardBg: "#ffffff",
        danger: "#e15759", warning: "#f59e0b", success: "#2ca02c",
        brand: "#8237bb",
      };

      const filePath = file.path || file.absolute_path || "";
      const fileName = file.name || filePath.split("/").pop() || "file";
      const safeName = fileName.replace(/[^a-z0-9-_.]/gi, "_");
      const stamp = new Date().toISOString().slice(0, 10);

      // Cover header
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(18);
      pdf.setTextColor(...brand);
      pdf.text("File Lifecycle Report", margin, margin + 6);
      pdf.setFontSize(13);
      pdf.setTextColor(40);
      pdf.text(fileName, margin, margin + 26);
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(110);
      pdf.text(
        `Generated ${new Date().toLocaleString()} · Path: ${filePath || "—"}`,
        margin, margin + 42,
      );

      const tableOpts = {
        margin: { left: margin, right: margin },
        styles: { fontSize: 9, cellPadding: 4, overflow: "linebreak" },
        headStyles: { fillColor: brand, textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: [248, 244, 253] },
      };
      const startY = (afterPrev = 18) =>
        (pdf.lastAutoTable ? pdf.lastAutoTable.finalY : margin + 56) + afterPrev;
      const sectionHeader = (title) => {
        const y = startY(22);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor(...brand);
        pdf.text(title, margin, y);
        pdf.setDrawColor(...brand);
        pdf.setLineWidth(0.6);
        pdf.line(margin, y + 3, pageW - margin, y + 3);
        return y + 10;
      };

      // ── File details ──────────────────────────────────────────────────────
      autoTable(pdf, {
        ...tableOpts,
        startY: sectionHeader("File details"),
        head: [["Field", "Value"]],
        body: [
          ["Name", fileName],
          ["Path", filePath || "—"],
          ["Type", file.type || file.file_type || "—"],
          ["Size", humanBytes(file.size || 0)],
          ["Versions", String(file.versionCount || file.versions || versions.length || 0)],
          (() => {
            const updated = file.updatedAt || file.updated_at;
            return ["Updated", updated ? `${fmtDate(updated)} ${fmtTime(updated)} (${relativeTime(updated)})` : "—"];
          })(),
          ["Tags", (file.tags || []).join(", ") || "—"],
          ["Status", "ALIVE"],
          ...(breadcrumbItems.length > 1 ? [[
            "Branch",
            breadcrumbItems.map((b) => b.label).join(" ▸ "),
          ]] : []),
        ],
        columnStyles: { 0: { cellWidth: 110, fontStyle: "bold" } },
      });

      // ── Summary stats ─────────────────────────────────────────────────────
      const distinctActors = new Set(events.map((e) => e.actor).filter(Boolean));
      const distinctCountries = new Set(events.map((e) => e.country).filter(Boolean));
      const forkOps = new Set(["CopyFile", "MoveFile", "MoveFolder"]);
      const forkCount = events.filter((e) => forkOps.has(e.op)).length;
      const deniedCount = events.filter((e) => e.success === false).length;

      autoTable(pdf, {
        ...tableOpts,
        startY: sectionHeader("Summary"),
        head: [["Metric", "Value"]],
        body: [
          ["Total events", String(events.length)],
          ["Distinct actors", String(distinctActors.size)],
          ["Distinct countries", String(distinctCountries.size)],
          ["Active shares", String(sharesForFile.length)],
          ["External recipients", String(externalForFile.length)],
          ["Forks (copy/move)", String(forkCount)],
          ["Denied attempts", String(deniedCount)],
          ["Path changes", String(pathsForFile.length)],
        ],
        columnStyles: { 0: { cellWidth: 180, fontStyle: "bold" } },
      });

      // ── Lifecycle stages — every event ────────────────────────────────────
      if (events.length) {
        autoTable(pdf, {
          ...tableOpts,
          startY: sectionHeader(`Lifecycle stages (${events.length})`),
          head: [["#", "When", "Op", "Actor", "Country", "Detail", "Granted by", "Result"]],
          body: events.map((e, i) => [
            String(i + 1),
            `${fmtDate(e.at)} ${fmtTime(e.at)}`,
            e.op,
            e.actor || "—",
            e.country || "—",
            e.detail || "—",
            grantedByFlat(e.permission_granted_by),
            e.success === false ? "denied" : "allowed",
          ]),
          styles: { ...tableOpts.styles, fontSize: 8 },
          columnStyles: {
            0: { cellWidth: 22, halign: "right" },
            1: { cellWidth: 78 },
            2: { cellWidth: 70 },
            5: { cellWidth: "auto" },
            7: { cellWidth: 50 },
          },
        });
      }

      // ── Versions ──────────────────────────────────────────────────────────
      if (versions.length) {
        autoTable(pdf, {
          ...tableOpts,
          startY: sectionHeader(`Version history (${versions.length})`),
          head: [["Kind", "Version", "When", "Actor", "Size", "Note"]],
          body: versions.map((v) => [
            v.kind,
            v.version,
            `${fmtDate(v.at)} ${fmtTime(v.at)}`,
            v.actor || "—",
            v.size || "—",
            v.label || "—",
          ]),
          columnStyles: { 5: { cellWidth: "auto" } },
        });
      }

      // ── Current shares ────────────────────────────────────────────────────
      if (sharesForFile.length) {
        autoTable(pdf, {
          ...tableOpts,
          startY: sectionHeader(`Current shares (${sharesForFile.length})`),
          head: [["Recipient", "Type", "Permission", "Granted by"]],
          body: sharesForFile.map((s) => {
            const recipient = s.recipient || s.subject || "—";
            const type = typeof s.type === "number"
              ? (s.type === 2 ? "team" : s.type === 1 ? "user" : "external")
              : (s.type || "—");
            const permission = typeof s.permission === "number"
              ? (s.permission === 3 ? "edit" : s.permission === 2 ? "write" : s.permission === 1 ? "read" : "—")
              : (s.permission || "—");
            return [
              recipient,
              type,
              permission,
              grantedByFlat(s.grantedBy || s.granted_by) || "—",
            ];
          }),
        });
      }

      // ── External exposure ─────────────────────────────────────────────────
      if (externalForFile.length) {
        autoTable(pdf, {
          ...tableOpts,
          startY: sectionHeader(`External exposure (${externalForFile.length})`),
          head: [["Email", "Domain", "Last access", "Dormant days", "Status"]],
          body: externalForFile.map((x) => [
            x.email,
            x.domain,
            `${fmtDate(x.last_activity)} (${relativeTime(x.last_activity)})`,
            String(x.dormant_days ?? 0),
            x.dormant_days >= 30 ? "dormant" : "active",
          ]),
        });
      }

      // ── Path history ──────────────────────────────────────────────────────
      if (pathsForFile.length) {
        autoTable(pdf, {
          ...tableOpts,
          startY: sectionHeader(`Path history (${pathsForFile.length})`),
          head: [["When", "Op", "From", "To", "Actor"]],
          body: pathsForFile.map((p) => {
            const at = p.timestamp || p.at;
            return [
              at ? `${fmtDate(at)} ${fmtTime(at)}` : "—",
              p.operation || p.op || "—",
              p.fromPath || p.from || "—",
              p.toPath || p.to || "—",
              p.actor || "—",
            ];
          }),
          columnStyles: { 2: { cellWidth: "auto" }, 3: { cellWidth: "auto" } },
        });
      }

      // ── Folder contents (folders only) ────────────────────────────────────
      if (isFolder && folderContents) {
        autoTable(pdf, {
          ...tableOpts,
          startY: sectionHeader(`Folder contents (${folderContents.children.length})`),
          head: [["Type", "Name", "Size"]],
          body: [
            ...folderContents.children.map((c) => {
              const cPath = c.path || c.absolute_path || "";
              return [
                c.type || c.file_type || "—",
                c.name || cPath.split("/").pop() || "—",
                humanBytes(c.size || 0),
              ];
            }),
            ["", "TOTAL", humanBytes(folderContents.totalSize)],
          ],
          columnStyles: { 0: { cellWidth: 70 }, 2: { cellWidth: 80, halign: "right" } },
        });

        const typeRows = Object.entries(folderContents.byType).map(([k, v]) => [k, String(v)]);
        if (typeRows.length) {
          autoTable(pdf, {
            ...tableOpts,
            startY: sectionHeader("Folder type breakdown"),
            head: [["File type", "Count"]],
            body: typeRows,
            columnStyles: { 1: { cellWidth: 80, halign: "right" } },
          });
        }
      }

      // ── Top actors: bar chart + table ─────────────────────────────────────
      if (events.length) {
        const counts = {};
        events.forEach((e) => { counts[e.actor] = (counts[e.actor] || 0) + 1; });
        const rows = Object.entries(counts)
          .map(([actor, count]) => [actor, String(count), `${((count / events.length) * 100).toFixed(1)}%`])
          .sort((a, b) => Number(b[1]) - Number(a[1]));

        const ordered = [...Object.entries(counts)].sort((a, b) => a[1] - b[1]);
        const actorChartOption = {
          tooltip: {},
          grid: { top: 10, right: 40, bottom: 36, left: 180 },
          xAxis: { type: "value", axisLabel: { color: pdfPalette.textSoft, fontSize: 11 }, splitLine: { lineStyle: { color: pdfPalette.grid } }, axisLine: { lineStyle: { color: pdfPalette.grid } } },
          yAxis: { type: "category", data: ordered.map(([a]) => a), axisLine: { lineStyle: { color: pdfPalette.grid } }, axisTick: { show: false }, axisLabel: { color: pdfPalette.textSoft, fontSize: 11 } },
          series: [{
            type: "bar",
            data: ordered.map(([, v]) => ({ value: v, itemStyle: { color: pdfPalette.brand, borderRadius: [0, 6, 6, 0] } })),
            label: { show: true, position: "right", color: pdfPalette.text, fontSize: 11 },
          }],
        };
        sectionHeader("Top actors");
        const img = await renderChartToImage(actorChartOption, { width: 900, height: 320 });
        addChartImage(pdf, img, { margin });
        autoTable(pdf, {
          ...tableOpts,
          startY: pdf.lastAutoTable.finalY + 10,
          head: [["Actor", "Events", "Share"]],
          body: rows,
          columnStyles: { 1: { cellWidth: 70, halign: "right" }, 2: { cellWidth: 70, halign: "right" } },
        });
      }

      // ── Operation mix by category: donut + table ──────────────────────────
      if (events.length) {
        const mix = {};
        const unknownOps = new Set();
        events.forEach((e) => {
          const cat = OP_CATEGORY[e.op] || "other";
          if (cat === "other") unknownOps.add(e.op);
          mix[cat] = (mix[cat] || 0) + 1;
        });
        if (unknownOps.size > 0) {
          console.debug("[OperationMix] ops falling into 'other':", [...unknownOps]);
        }
        const total = events.length;
        const rows = Object.entries(mix)
          .map(([cat, n]) => [cat, String(n), `${((n / total) * 100).toFixed(1)}%`])
          .sort((a, b) => Number(b[1]) - Number(a[1]));

        const CAT_COLOR = {
          creation: "#10b981", modification: "#3b82f6", sharing: "#8237bb",
          location: "#6366f1", read: "#94a3b8", destructive: "#ef4444",
          fork: "#f59e0b", other: "#cbd5e1",
        };
        const opMixOption = {
          tooltip: {},
          legend: { type: "scroll", orient: "horizontal", bottom: 0, left: "center", textStyle: { color: pdfPalette.text, fontSize: 11 }, itemWidth: 10, itemHeight: 10, itemGap: 14 },
          series: [{
            type: "pie",
            radius: ["48%", "70%"],
            center: ["50%", "44%"],
            avoidLabelOverlap: true,
            itemStyle: { borderColor: pdfPalette.cardBg, borderWidth: 2, borderRadius: 4 },
            label: { show: false },
            labelLine: { show: false },
            data: Object.entries(mix).map(([name, value]) => ({ name, value, itemStyle: { color: CAT_COLOR[name] || CAT_COLOR.other } })),
          }],
        };
        sectionHeader("Operation mix");
        const img = await renderChartToImage(opMixOption, { width: 720, height: 360 });
        addChartImage(pdf, img, { margin });
        autoTable(pdf, {
          ...tableOpts,
          startY: pdf.lastAutoTable.finalY + 10,
          head: [["Category", "Count", "Share"]],
          body: rows,
          columnStyles: { 1: { cellWidth: 70, halign: "right" }, 2: { cellWidth: 70, halign: "right" } },
        });
      }

      // ── Read locations: bar chart + table ─────────────────────────────────
      const locCounts = {};
      events.forEach((e) => { if (e.country) locCounts[e.country] = (locCounts[e.country] || 0) + 1; });
      const locRows = Object.entries(locCounts).sort((a, b) => b[1] - a[1]);
      if (locRows.length) {
        const TRUSTED = new Set(["India"]);
        const BLOCKED = new Set(["Russia", "North Korea", "Vietnam", "Nigeria"]);
        const tier = (c) => BLOCKED.has(c) ? "blocked" : TRUSTED.has(c) ? "trusted" : "foreign";
        const tierColor = (c) => BLOCKED.has(c) ? pdfPalette.danger : TRUSTED.has(c) ? pdfPalette.success : pdfPalette.warning;

        const ordered = [...locRows].sort((a, b) => a[1] - b[1]);
        const locChartOption = {
          tooltip: {},
          grid: { top: 10, right: 56, bottom: 36, left: 160 },
          xAxis: { type: "value", axisLabel: { color: pdfPalette.textSoft, fontSize: 11 }, splitLine: { lineStyle: { color: pdfPalette.grid } }, axisLine: { lineStyle: { color: pdfPalette.grid } } },
          yAxis: { type: "category", data: ordered.map(([c]) => c), axisLine: { lineStyle: { color: pdfPalette.grid } }, axisTick: { show: false }, axisLabel: { color: pdfPalette.text, fontSize: 11 } },
          series: [{
            type: "bar",
            data: ordered.map(([c, v]) => ({ value: v, itemStyle: { color: tierColor(c), borderRadius: [0, 6, 6, 0] } })),
            label: { show: true, position: "right", color: pdfPalette.text, fontSize: 11 },
          }],
        };
        sectionHeader("Read locations");
        const img = await renderChartToImage(locChartOption, { width: 900, height: 320 });
        addChartImage(pdf, img, { margin });
        autoTable(pdf, {
          ...tableOpts,
          startY: pdf.lastAutoTable.finalY + 10,
          head: [["Country", "Events", "Trust tier"]],
          body: locRows.map(([c, n]) => [c, String(n), tier(c)]),
          columnStyles: { 1: { cellWidth: 70, halign: "right" }, 2: { cellWidth: 90 } },
        });
      }

      // Footer pagination
      const total = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(8);
        pdf.setTextColor(140);
        pdf.text(
          `${fileName} · Page ${i} of ${total}`,
          pageW - margin,
          pdf.internal.pageSize.getHeight() - 14,
          { align: "right" },
        );
      }

      pdf.save(`${safeName}-lifecycle-${stamp}.pdf`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Export failed:", err);
    } finally {
      setExporting(false);
    }
  };

  // ── Derived data for supporting cards ────────────────────────────────────

  const versions = useMemo(() => deriveVersions(events), [events]);

  const isFolder = (file?.type || file?.file_type) === "folder";

  // Folder contents — derive from FILES list (children whose parent path
  // matches this folder's path). Real backend will replace via SDK.
  const folderContents = useMemo(() => {
    if (!isFolder || !file) return null;
    const folderPath = file.path || file.absolute_path || "";
    if (!folderPath) return null;
    const prefix = folderPath.endsWith("/") ? folderPath : folderPath + "/";
    const children = allFiles.filter((f) => {
      const cPath = f.path || f.absolute_path || "";
      return f.uuid !== file.uuid &&
        cPath.startsWith(prefix) &&
        // direct children only (no nested folders' contents)
        cPath.slice(prefix.length).split("/").filter(Boolean).length === 1;
    });
    const byType = {};
    children.forEach((c) => {
      const t = c.type || c.file_type || "other";
      byType[t] = (byType[t] || 0) + 1;
    });
    return { children, byType, totalSize: children.reduce((a, c) => a + (c.size || 0), 0) };
  }, [allFiles, file, isFolder]);

  const folderTypesOption = useMemo(() => {
    if (!folderContents) return null;
    const data = Object.entries(folderContents.byType)
      .map(([name, value]) => ({ name, value }));
    if (!data.length) return null;
    return {
      backgroundColor: "transparent",
      color: TABLEAU_10,
      tooltip: { backgroundColor: palette.tooltipBg, borderColor: palette.tooltipBorder, textStyle: { color: palette.text } },
      legend: {
        type: "scroll",
        orient: "vertical",
        right: 4,
        top: "middle",
        textStyle: { color: palette.text, fontSize: 11 },
        itemWidth: 10,
        itemHeight: 10,
      },
      series: [{
        type: "pie",
        radius: ["52%", "78%"],
        center: ["32%", "50%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: palette.cardBg, borderWidth: 2, borderRadius: 4 },
        label: { show: false },
        labelLine: { show: false },
        data,
      }],
    };
  }, [folderContents, palette]);

  const sharesForFile = sdkShares.length > 0 ? sdkShares : (fileFromAll ? (currentShares[fileFromAll.uuid] || []) : []);
  const externalForFile = sdkShares.length > 0
    ? sdkShares
        .filter((s) => {
          const t = typeof s.type === "number" ? s.type : 0;
          return t !== 1 && t !== 2; // 1=SHARE_TYPE_USER, 2=SHARE_TYPE_TEAM → anything else is external
        })
        .map((s) => {
          const userEvents = sdkEvents.filter((e) => e.actor === s.recipient);
          const lastActivity = userEvents.length > 0
            ? Math.max(...userEvents.map((e) => Number(e.timestamp || Date.parse(e.at))))
            : 0;
          return {
            email: s.recipient,
            domain: s.recipient?.split("@")[1] || "—",
            last_activity: lastActivity,
            dormant_days: lastActivity ? Math.floor((Date.now() - lastActivity) / 86400000) : 0,
          };
        })
    : (fileFromAll ? (externalExposure[fileFromAll.uuid] || []) : []);
  const pathsForFile = sdkPaths.length > 0 ? sdkPaths : (fileFromAll ? (pathHistory[fileFromAll.uuid] || []) : []);

  const topActorsOption = useMemo(() => {
    const raw = sdkActors.length > 0 ? sdkActors : [];
    if (!raw.length && !events.length) return null;
    
    let ordered = [];
    if (sdkActors.length > 0) {
      ordered = sdkActors.map(a => ({ actor: a.actor, value: Number(a.eventCount) }))
        .sort((a, b) => a.value - b.value);
    } else {
      const counts = {};
      events.forEach((e) => { counts[e.actor] = (counts[e.actor] || 0) + 1; });
      ordered = Object.entries(counts)
        .map(([k, v]) => ({ actor: k, value: v }))
        .sort((a, b) => a.value - b.value);
    }

    if (!ordered.length) return null;

    return {
      backgroundColor: "transparent",
      grid: { top: 10, right: 40, bottom: 36, left: 180 },
      tooltip: { trigger: "axis", backgroundColor: palette.tooltipBg, borderColor: palette.tooltipBorder, textStyle: { color: palette.text } },
      xAxis: {
        type: "value",
        axisLine:  { lineStyle: { color: palette.grid } },
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      yAxis: {
        type: "category",
        data: ordered.map((r) => r.actor),
        axisLine: { lineStyle: { color: palette.grid } },
        axisTick: { show: false },
        axisLabel: { color: palette.textSoft, fontSize: 11, width: 165, overflow: "truncate", ellipsis: "..." },
      },
      series: [{
        type: "bar",
        data: ordered.map((r) => ({
          value: r.value,
          itemStyle: { color: palette.brand, borderRadius: [0, 6, 6, 0] },
        })),
        label: { show: true, position: "right", color: palette.text, fontSize: 11 },
      }],
    };
  }, [events, sdkActors, palette]);

  const opMixOption = useMemo(() => {
    if (!events.length) return null;
    const mix = {};
    const unknownOps = new Set();
    events.forEach((e) => {
      const cat = OP_CATEGORY[e.op] || "other";
      if (cat === "other") unknownOps.add(e.op);
      mix[cat] = (mix[cat] || 0) + 1;
    });
    if (unknownOps.size > 0) {
      console.debug("[OperationMix] ops falling into 'other':", [...unknownOps]);
    }
    const COLORS = {
      creation:     "#10b981",
      modification: "#3b82f6",
      sharing:      "#8237bb",
      location:     "#6366f1",
      read:         "#94a3b8",
      destructive:  "#ef4444",
      other:        "#cbd5e1",
    };
    const data = Object.entries(mix).map(([name, value]) => ({ name, value, itemStyle: { color: COLORS[name] || COLORS.other } }));
    const total = data.reduce((a, d) => a + d.value, 0);
    return {
      backgroundColor: "transparent",
      tooltip: {
        backgroundColor: palette.tooltipBg,
        borderColor: palette.tooltipBorder,
        textStyle: { color: palette.text },
        formatter: (p) => `<b>${p.name}</b><br/>${p.value} ops · ${total ? ((p.value / total) * 100).toFixed(1) : 0}%`,
      },
      legend: {
        type: "scroll",
        orient: "vertical",
        right: 4,
        top: "middle",
        textStyle: { color: palette.text, fontSize: 11 },
        itemWidth: 10,
        itemHeight: 10,
      },
      series: [{
        type: "pie",
        radius: ["52%", "78%"],
        center: ["32%", "50%"],
        avoidLabelOverlap: true,
        itemStyle: { borderColor: palette.cardBg, borderWidth: 2, borderRadius: 4 },
        label: { show: false },
        labelLine: { show: false },
        data,
      }],
    };
  }, [events, palette]);

  const readLocationsOption = useMemo(() => {
    const raw = sdkCountries.length > 0 ? sdkCountries : [];
    if (!raw.length && !events.length) return null;

    let ordered = [];
    if (sdkCountries.length > 0) {
      ordered = sdkCountries.map(c => ({ country: c.country, value: Number(c.eventCount), trustTier: c.trustTier }))
        .sort((a, b) => a.value - b.value);
    } else {
      const counts = {};
      events.forEach((e) => {
        if (!e.country) return;
        counts[e.country] = (counts[e.country] || 0) + 1;
      });
      ordered = Object.entries(counts)
        .map(([country, value]) => ({ country, value }))
        .sort((a, b) => a.value - b.value);
    }

    if (!ordered.length) return null;
    
    // Trust tier mapping from SDK
    const tierColor = (r) => {
      if (sdkCountries.length > 0) {
        if (r.trustTier === 1) return (palette.success || "#10b981");
        if (r.trustTier === 3) return (palette.danger || "#ef4444");
        return (palette.warning || "#f59e0b");
      }
      // Fallback to legacy mock logic
      const TRUSTED = new Set(["India"]);
      const BLOCKED = new Set(["Russia", "North Korea", "Vietnam", "Nigeria"]);
      return BLOCKED.has(r.country) ? (palette.danger || "#ef4444") :
             TRUSTED.has(r.country) ? (palette.success || "#10b981") :
                              (palette.warning || "#f59e0b");
    };

    return {
      backgroundColor: "transparent",
      grid: { top: 10, right: 56, bottom: 36, left: 160 },
      tooltip: { trigger: "axis", backgroundColor: palette.tooltipBg, borderColor: palette.tooltipBorder, textStyle: { color: palette.text } },
      xAxis: {
        type: "value",
        axisLine:  { lineStyle: { color: palette.grid } },
        splitLine: { lineStyle: { color: palette.grid } },
        axisLabel: { color: palette.textSoft, fontSize: 11 },
      },
      yAxis: {
        type: "category",
        data: ordered.map((r) => r.country),
        axisLine: { lineStyle: { color: palette.grid } },
        axisTick: { show: false },
        axisLabel: { color: palette.text, fontSize: 11, width: 140, overflow: "truncate", ellipsis: "..." },
      },
      series: [{
        type: "bar",
        data: ordered.map((r) => ({
          value: r.value,
          itemStyle: { color: tierColor(r), borderRadius: [0, 6, 6, 0] },
        })),
        label: { show: true, position: "right", color: palette.text, fontSize: 11 },
      }],
    };
  }, [events, sdkCountries, palette]);

  // ── Branch handling: clicking "Follow" on a CopyFile / MoveFile step jumps
  //    into the new file's lifecycle. The breadcrumb tracks ancestry so the
  //    user can step back at any depth.
  const handleFollow = (step) => {
    // 1. Try destination_file_id from SDK first
    if (step.destination_file_id) {
      pushBranch(step.destination_file_id);
      return;
    }

    // 2. Legacy fallback: try to resolve by path in mock data
    const detail = (step.detail || "").toLowerCase();
    const candidate = allFiles.find((f) =>
      detail.includes(f.absolute_path.split("/").pop().toLowerCase()),
    );
    if (candidate && candidate.uuid !== file?.uuid) {
      pushBranch(candidate.uuid);
    } else {
      // eslint-disable-next-line no-console
      console.info("Follow: destination file not found in SDK or mock data.");
    }
  };

  const breadcrumbItems = (breadcrumb || []).map((uuid) => ({
    uuid,
    label: allFiles.find((f) => f.uuid === uuid)?.absolute_path.split("/").pop()
         || (sdkFile?.uuid === uuid
             ? (sdkFile.name || sdkFile.path?.split("/").pop() || uuid)
             : uuid),
  }));

  // ── Empty state — no file selected → show panel-scoped picker ────────────
  // Note: gate on `selectedFileUuid` (not `file`). When a sibling dashboard
  // navigates here with a uuid, `file` is briefly null until GetFileDetails
  // resolves; rendering the picker in that gap caused a flash.

  if (!selectedFileUuid) {
    return (
      <div className="ysd-page files-page">
        <div className="files-page__empty-hero">
          <h2>Follow a file or folder</h2>
          <p>Pick something to see its full lifecycle: who created, edited, shared, moved, or accessed it.</p>
        </div>
        <FileSearchModal
          isOpen
          variant="panel"
          dismissible={false}
          onClose={() => {}}
          onSelectFile={(uuid) => setSelectedFile(uuid)}
        />
      </div>
    );
  }

  if (!file) {
    return (
      <div className="ysd-page files-page">
        {loadingFile || !errorFile ? (
          <Loader message="Loading file…" minHeight={320} />
        ) : (
          <ErrorHint message={errorFile} />
        )}
      </div>
    );
  }

  // ── Active file view ──────────────────────────────────────────────────────

  return (
    <div className="ysd-page files-page">
      {/* Header */}
      <div className="files-page__head">
        <div className="files-page__head-left">
          <div className="files-page__head-icon">{iconFor(file.type || file.file_type)}</div>
          <div className="files-page__head-body">
            <strong>{file.name || file.path?.split("/").pop() || "Unnamed File"}</strong>
            <small className="mono">
              {file.path || "/"}
            </small>
            <small>
              {humanBytes(file.size)} · {file.type || "file"} · {file.version_count || 0} versions ·
              updated {relativeTime(file.updated_at)}
            </small>
            {breadcrumbItems.length > 1 && (
              <nav className="files-page__breadcrumb" aria-label="Branch path">
                {breadcrumbItems.map((b, i) => (
                  <React.Fragment key={`${b.uuid}-${i}`}>
                    {i > 0 && <span className="files-page__breadcrumb-sep">▸</span>}
                    <button
                      type="button"
                      className={`files-page__breadcrumb-link${i === breadcrumbItems.length - 1 ? " is-current" : ""}`}
                      onClick={() => popToBreadcrumb(i)}
                      disabled={i === breadcrumbItems.length - 1}
                    >
                      {b.label}
                    </button>
                  </React.Fragment>
                ))}
              </nav>
            )}
          </div>
        </div>
        <div className="files-page__head-right">
          <div className="ysd-chips">
            {(file.tags || []).map((t) => <Badge key={t} tone="brand" title={`#${t}`}>#{t}</Badge>)}
            {(() => {
              const statusLabel = derivedStatus
                ?? (file.status === 2 ? "DELETED"
                  : file.status === 3 ? "ARCHIVED"
                  : file.status === 4 ? "QUARANTINED"
                  : "ALIVE");
              
              const statusTone = derivedStatus === "REMOVED"
                ? "danger"
                : (file.status === 2 ? "danger"
                  : file.status === 3 ? "warning"
                  : "success");

              return (
                <Badge tone={statusTone} dot>
                  {statusLabel}
                </Badge>
              );
            })()}
          </div>
          <div className="files-page__actions">
            <button
              type="button"
              className="files-page__action-btn"
              onClick={() => setPickerOpen(true)}
              title="Find another file (⌘K)"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" /><path d="M21 21l-4.35-4.35" />
              </svg>
              Find another file
            </button>
            <button
              type="button"
              className="files-page__action-btn files-page__action-btn--primary"
              onClick={exportPdf}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export PDF"}
            </button>
          </div>
        </div>
      </div>

      {/* ROW 1 — Step navigator (centerpiece) */}
      <div style={{ position: "relative" }}>
        {loadingEvents ? <Loader overlay /> :
         errorEvents ? <ErrorHint message={errorEvents} /> :
         events.length === 0 ? <EmptyHint>No events recorded for this file.</EmptyHint> :
         <StepNavigator events={events} onFollow={handleFollow} />}
      </div>


      {/* ROW 2 — Version history (file) OR Current contents (folder) */}
      {isFolder ? (
        <ChartCard
          title="Current contents"
          subtitle={
            folderContents
              ? `${folderContents.children.length} item${folderContents.children.length === 1 ? "" : "s"} · ${humanBytes(folderContents.totalSize)}`
              : "No contents recorded"
          }
        >
          {loadingFile ? <Loader message="Loading contents…" minHeight={220} /> :
           errorFile ? <ErrorHint message={errorFile} /> :
           folderContents && folderContents.children.length === 0 ? (
            <EmptyHint>This folder is empty.</EmptyHint>
          ) : (
            <div className="files-page__folder-contents">
              <div className="files-page__folder-contents-chart">
                {folderTypesOption ? <EChart option={folderTypesOption} height={220} /> : null}
              </div>
              <div className="files-page__folder-contents-list">
                <div className="files-page__folder-contents-title">
                  Recent files ({Math.min(5, folderContents?.children?.length || 0)} of {folderContents?.children?.length || 0})
                </div>
                {[...(folderContents?.children || [])]
                  .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
                  .slice(0, 5)
                  .map((c) => (
                    <div key={c.uuid} className="files-page__folder-row">
                      <span className="files-page__folder-row-icon">{iconFor(c.file_type)}</span>
                      <button
                        type="button"
                        className="files-page__folder-row-name"
                        onClick={() => pushBranch(c.uuid)}
                        title="Open in lifecycle view"
                      >
                        {c.absolute_path.split("/").pop()}
                      </button>
                      <small>{humanBytes(c.size)} · updated {relativeTime(c.updated_at)}</small>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </ChartCard>
      ) : (
        <ChartCard
          title="Version history"
          subtitle={`${(sdkVersions.length > 0 ? sdkVersions : versions).length} version event${(sdkVersions.length > 0 ? sdkVersions : versions).length === 1 ? "" : "s"} — created · updated · restored · removed · deleted`}
        >
          {loadingVersions ? <Loader message="Loading versions…" minHeight={200} /> :
           errorVersions ? <ErrorHint message={errorVersions} /> :
           (sdkVersions.length > 0 ? sdkVersions : versions).length === 0 ? (
            <EmptyHint>No version events yet.</EmptyHint>
          ) : (
            <>
              <div className="ysd-journey-legend">
                {Object.entries(VERSION_KIND).map(([k, v]) => (
                  <span key={k}>
                    <span className="dot" style={{ background: v.color }} />
                    {k}
                  </span>
                ))}
              </div>
              <div className="ysd-versions-viewport">
                <div className="ysd-versions">
                  {(sdkVersions.length > 0 ? sdkVersions : versions).map((v, i) => {
                    const kindLabel = typeof v.kind === "number" ? SDK_VERSION_KIND[v.kind] : v.kind;
                    const meta = VERSION_KIND[kindLabel] || VERSION_KIND.Updated;
                    return (
                      <React.Fragment key={i}>
                        <div className="ysd-version" style={{ "--category-color": meta.color }}>
                          <div className="ysd-version__dot">{truncVersion(v.version) || i+1}</div>
                          <div className="ysd-version__kind">{meta.icon} {kindLabel}</div>
                          <div className="ysd-version__by">{String(v.actor).split("@")[0]}</div>
                          <div className="ysd-version__date">{fmtDate(v.timestamp || v.at)}</div>
                          <div className="ysd-version__time">{fmtTime(v.timestamp || v.at)}</div>
                          {v.size && <div className="ysd-version__size">{humanBytes(v.size)}</div>}
                        </div>
                        {i < (sdkVersions.length > 0 ? sdkVersions : versions).length - 1 && <div className="ysd-version-link" />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </ChartCard>
      )}


      {/* ROW 3 — Sharing surface */}
      <div className="ysd-grid ysd-grid--2">
        <ChartCard title="Current shares" subtitle="Recipients with access right now" span={4}>
          {loadingShares ? <Loader message="Loading shares…" minHeight={200} /> :
           errorShares ? <ErrorHint message={errorShares} /> :
           sharesForFile.length === 0 ? (
            <EmptyHint>No active shares.</EmptyHint>
          ) : (
            <div className="ysd-table__scroll">
              <table className="ysd-table">
                <thead>
                  <tr><th>Recipient</th><th>Type</th><th>Permission</th><th>Granted by</th></tr>
                </thead>
                <tbody>
                  {sharesForFile.map((s, i) => {
                    const recipient = s.recipient || s.subject;
                    const type = typeof s.type === "number" ? (s.type === 2 ? "team" : "user") : s.type;
                    const permission = typeof s.permission === "number" ? (s.permission === 2 ? "write" : "read") : s.permission;
                    return (
                      <tr key={i}>
                        <td><span className="ysd-mono">{recipient}</span></td>
                        <td>
                          <Badge tone={type === "external" ? "danger" : type === "team" ? "info" : "neutral"} dot>
                            {type}
                          </Badge>
                        </td>
                        <td>{permission}</td>
                        <td><GrantedBySourceCard g={s.grantedBy || s.granted_by} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </ChartCard>


        <ChartCard
          title="External exposure"
          subtitle={
            externalForFile.length === 0
              ? "No external recipients"
              : sdkSummary ? `${sdkSummary.externalRecipients} external recipient${sdkSummary.externalRecipients === 1 ? "" : "s"}` : `${externalForFile.length} external recipient${externalForFile.length === 1 ? "" : "s"}`
          }
          span={4}
        >
          {loadingSummary ? <Loader message="Loading summary…" minHeight={200} /> :
           errorSummary ? <ErrorHint message={errorSummary} /> :
           externalForFile.length === 0 ? (
            <EmptyHint>This file is not shared outside the org.</EmptyHint>
          ) : (
            <div className="ysd-table__scroll">
              <table className="ysd-table">
                <thead>
                  <tr><th>Email</th><th>Domain</th><th>Last access</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {externalForFile.map((x, i) => {
                    const dormant = x.dormant_days >= 30;
                    return (
                      <tr key={i}>
                        <td><span className="ysd-mono">{x.email}</span></td>
                        <td>{x.domain}</td>
                        <td><small>{relativeTime(x.last_activity)}</small></td>
                        <td>
                          <Badge tone={dormant ? "danger" : "success"} dot>
                            {dormant ? `dormant ${x.dormant_days}d` : "active"}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </ChartCard>


        <ChartCard
          title="Path history"
          subtitle="Where this file has lived"
          span={4}
        >
          {loadingPaths ? <Loader message="Loading paths…" minHeight={200} /> :
           errorPaths ? <ErrorHint message={errorPaths} /> :
           pathsForFile.length === 0 ? (
            <EmptyHint>No path changes recorded.</EmptyHint>
          ) : (
            <div className="files-page__path-history">
              {pathsForFile.map((p, i) => {
                const at = p.timestamp || p.at;
                const op = p.operation || p.op;
                const from = p.fromPath || p.from;
                const to = p.toPath || p.to;
                return (
                  <div key={i} className="files-page__path-step">
                    <div className="files-page__path-when">
                      <span className="ysd-mono">{fmtDate(at)}</span>
                      <small>{op}</small>
                    </div>
                    <div className="files-page__path-arrow">
                      {from && from !== "—" ? (
                        <>
                          <span className="ysd-mono">{from}</span>
                          <span className="files-page__path-arrow-mark">→</span>
                          <span className="ysd-mono">{to}</span>
                        </>
                      ) : (
                        <span className="ysd-mono">{to}</span>
                      )}
                    </div>
                    <small className="files-page__path-actor">by {p.actor}</small>
                  </div>
                );
              })}
            </div>
          )}
        </ChartCard>

      </div>

      {/* ROW 4 — Activity */}
      <div className="ysd-grid ysd-grid--2">
        <ChartCard
          title="Top actors"
          subtitle="Users who have touched this file the most"
          span={6}
        >
          {loadingActors ? <Loader message="Loading actors…" minHeight={280} /> :
           errorActors ? <ErrorHint message={errorActors} /> :
           topActorsOption ? <EChart option={topActorsOption} height={280} /> : <EmptyHint>No events yet.</EmptyHint>}
        </ChartCard>
        <ChartCard
          title="Operation mix"
          subtitle="Share of reads / writes / shares / location changes"
          span={6}
        >
          {loadingEvents ? <Loader message="Loading mix…" minHeight={280} /> :
           errorEvents ? <ErrorHint message={errorEvents} /> :
           opMixOption ? <EChart option={opMixOption} height={280} /> : <EmptyHint>No events yet.</EmptyHint>}
        </ChartCard>
      </div>


      {/* ROW 5 — Read locations */}
      <ChartCard
        title="Read locations"
        subtitle="Where access events came from · green=trusted, amber=allowed-foreign, red=blocked"
      >
        {loadingCountries ? <Loader message="Loading locations…" minHeight={280} /> :
         errorCountries ? <ErrorHint message={errorCountries} /> :
         readLocationsOption ? <EChart option={readLocationsOption} height={280} /> : <EmptyHint>No geo data on this file&apos;s events.</EmptyHint>}
      </ChartCard>


      {/* On-demand picker — re-opens via the header button or ⌘K */}
      <FileSearchModal
        isOpen={pickerOpen}
        variant="viewport"
        onClose={() => setPickerOpen(false)}
        onSelectFile={(uuid) => {
          setSelectedFile(uuid);
          setPickerOpen(false);
        }}
      />
    </div>
  );
};

export default FilesDashboard;
